//#region IMPORT
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PPE_BASIC_03 from './PPE_BASIC_03';
import { FilterEquipOptionsByUserDepCd } from './PPE_BASIC_03_ColumnDefs';
import { useNavigate } from 'react-router-dom';
import Commons from '../../../../Services/Common/Common';
import Global from '../../../../Services/Common/Global';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { closeLoading, showLoading } from '../../../../Reducer/LoadingReducer';
import dayjs from 'dayjs';
import { useSearch1, useSave1, useSearch2, useDelete1 } from './PPE_BASIC_03_QUERY';
import { BuildPpeBasic03LedgerExcelBuffer } from './PPE_BASIC_03_ExcelExport';
import { PPE_BASIC_03_Schema } from './PPE_BASIC_03_Schema';
import { useSave } from '../../../../hooks/useSaveN';
import useDelete from '../../../../hooks/useDeleteN';
import MemberInfoModal from '../../../../Components/MemberInfoModal/MemberInfoModal';
//#endregion

/** 저장 API 결과 행별 성공 (배치 1건 / 행별 N건) */
const IsPpeSaveDetailRowSuccess = (detail, idx, changedLen) => {
  const list = detail ?? [];
  if (list.length === 0) return false;
  if (list.length === 1 && changedLen > 1) {
    const one = list[0];
    return one?.isSuccess === true || one?.IsSuccess === true;
  }
  const item = list[idx];
  return item?.isSuccess === true || item?.IsSuccess === true;
};

const PPE_ISSUE_FCM_GUBUN = 'AL_004_001';

/** SAC_BASICF SignRequest — GetMember로 수신자 FCM 토큰 조회 */
const FetchMemberForPpeFcmLogic1 = async (userId) => {
  try {
    const res = await Commons.Axios(
      'post',
      Global.base.useUrl + Global.urls.Utility.getMember,
      { Userid: Commons.EncryptAES(userId) }
    );
    const apiRes = res.data;
    const data = apiRes?.data;
    if (!apiRes?.isSuccess || !data) {
      return { ID: userId, NAME: '', FCM_TOKEN: '' };
    }
    const token = data.FCM_TOKEN || data.fcmToken || data.FcmToken || '';
    const name = data.NAME || data.name || '';
    return {
      ID: userId,
      NAME: String(name || ''),
      FCM_TOKEN: String(token || ''),
    };
  } catch {
    return { ID: userId, NAME: '', FCM_TOKEN: '' };
  }
};

/**
 * SAC_BASICF SignRequest(_value1==='0')와 동일: 수신자 조회 → 토큰 필터 → 건별 FCM → 응답 검증 → 통합 스낵바
 */
const SendFcmToPpeIssueRecipientsLogic1 = async ({
  changedRows,
  saveDetailData,
  userInfo,
  dispatch,
  currentYear,
}) => {
  const requestUserId = userInfo?.userid;
  if (
    !requestUserId ||
    !Array.isArray(changedRows) ||
    changedRows.length === 0 ||
    !dispatch
  ) {
    return;
  }

  const detail = saveDetailData ?? [];
  const allSucceeded =
    detail.length > 0 &&
    detail.every((r) => r?.isSuccess === true || r?.IsSuccess === true);
  if (!allSucceeded) return;

  const changedLen = changedRows.length;
  const byId = new Map();
  changedRows.forEach((row, idx) => {
    if (!IsPpeSaveDetailRowSuccess(detail, idx, changedLen)) return;
    const id = String(row?.userId ?? '').trim();
    if (!id) return;
    if (!byId.has(id)) {
      byId.set(id, {
        row,
        name: String(row?.userNm ?? '').trim() || '알 수 없음',
      });
    }
  });
  const uniqueIds = [...byId.keys()];
  if (!uniqueIds.length) return;

  const profileList = await Promise.all(
    uniqueIds.map((id) => FetchMemberForPpeFcmLogic1(id))
  );
  const attendeeList = profileList.map((p, i) => ({
    ID: p.ID,
    NAME:
      (p.NAME && p.NAME.trim()) || byId.get(uniqueIds[i])?.name || '알 수 없음',
    FCM_TOKEN: p.FCM_TOKEN || '',
  }));

  if (!attendeeList[0]?.ID || String(attendeeList[0].ID).trim() === '') {
    dispatch(
      showSnackbar({
        message: '지급 대상 정보를 확인할 수 없습니다.',
        type: 'warning',
      })
    );
    return;
  }

  const validAttendees = attendeeList.filter((attendee) => {
    const hasValidToken =
      attendee.FCM_TOKEN &&
      attendee.FCM_TOKEN.trim() !== '' &&
      attendee.FCM_TOKEN !== '0' &&
      attendee.FCM_TOKEN !== 'null' &&
      attendee.FCM_TOKEN !== 'undefined';
    return hasValidToken;
  });

  const invalidAttendeesCount = attendeeList.length - validAttendees.length;

  const year = String(currentYear ?? dayjs().format('YYYY'));
  const fcmUrl = Global.base.useUrl + Global.urls.FCM.fcm;

  const results = await Promise.allSettled(
    validAttendees.map(async (item) => {
      try {
        const row = byId.get(item.ID)?.row;
        const equipNm = String(row?.equipNm ?? '').trim() || '보호구';
        const responseDataRaw = `${String(item.ID)}|${year}|${String(row?.custCd ?? '')}`;
        const payload = [
          {
            ResponseId: Commons.EncryptAES(item.ID),
            RequestId: Commons.EncryptAES(userInfo.userid),
            Title: Commons.EncryptAES('보호구 지급'),
            Body: Commons.EncryptAES(
              `${equipNm} 지급이 등록되었습니다. 확인해주세요.`
            ),
            Route: Commons.EncryptAES('/M_Equipment'),
            ResponseData: Commons.EncryptAES(responseDataRaw),
            Gubun: Commons.EncryptAES(PPE_ISSUE_FCM_GUBUN),
            FcmToken: item.FCM_TOKEN || '',
          },
        ];
        const result = await Commons.Axios('post', fcmUrl, payload);

        let data = result.data;
        let responseData = null;

        if (Array.isArray(data) && data.length > 0) {
          responseData = data[0];
        } else if (data && typeof data === 'object' && !Array.isArray(data)) {
          responseData = data;
        }

        if (responseData) {
          const isSuccess =
            responseData.isSuccess !== undefined
              ? responseData.isSuccess
              : responseData.IsSuccess;

          if (
            isSuccess === false ||
            responseData.type === 'Error' ||
            responseData.Type === 'Error'
          ) {
            const errorMessage =
              responseData.message || responseData.Message || '알림 전송 실패';
            return {
              success: false,
              error: errorMessage,
              name: item.NAME,
            };
          }
          if (isSuccess === true) {
            return { success: true, name: item.NAME };
          }
          if (responseData.StatusType && responseData.StatusType !== 'ERROR') {
            return { success: true, name: item.NAME };
          }
        }

        if (
          Array.isArray(data) &&
          data.length > 0 &&
          (data[0].type === 'Error' || data[0].Type === 'Error')
        ) {
          const errorMessage =
            data[0].message || data[0].Message || '알림 전송 실패';
          return {
            success: false,
            error: errorMessage,
            name: item.NAME,
          };
        }

        if (data === 'Fail' || responseData === 'Fail') {
          return {
            success: false,
            error: '사용자가 어플에 로그인하지 않았습니다.',
            name: item.NAME,
          };
        }

        return {
          success: false,
          error: '알 수 없는 응답',
          name: item.NAME,
        };
      } catch (error) {
        return { success: false, error: error.message, name: item.NAME };
      }
    })
  );

  const successResults = [];
  const failResults = [];
  const errorResults = [];

  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      if (result.value.success) {
        successResults.push(result.value);
      } else {
        failResults.push(result.value);
      }
    } else {
      errorResults.push({
        name: validAttendees[index]?.NAME || '알 수 없음',
        error: result.reason?.message || '알 수 없는 오류',
      });
    }
  });

  const successCount = successResults.length;
  const failCount = failResults.length;
  const errorCount = errorResults.length;
  let message = '';
  let messageType = 'info';

  if (successCount > 0) {
    const successNames = successResults.map((r) => r.name).join(', ');
    message += `${successCount}명에게 보호구 지급 알림 전송 완료: ${successNames}`;
    messageType = 'success';
  }

  if (failCount > 0) {
    const failNames = failResults.map((r) => r.name).join(', ');
    if (message) message += '\n';
    message += `${failCount}명 전송 실패: ${failNames}`;
    if (messageType === 'success') messageType = 'warning';
  }

  if (errorCount > 0) {
    const errorNames = errorResults.map((r) => r.name).join(', ');
    if (message) message += '\n';
    message += `${errorCount}명 전송 중 오류 발생: ${errorNames}`;
    messageType = 'error';
  }

  if (invalidAttendeesCount > 0) {
    if (message) message += '\n';
    message += `${invalidAttendeesCount}명은 알림을 받을 수 없습니다. (앱 미설치 또는 로그인 안됨)`;
    if (messageType === 'success') messageType = 'info';
  }

  if (message) {
    dispatch(
      showSnackbar({
        message,
        type: messageType,
      })
    );
  }
};

function PPE_BASIC_03F(props) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const searchInfo = useSelector((state) => state.SearchInfo);

  //왼쪽 그리드
  const [rowData1, setRowData1] = useState([]); // 지급대장 그리드 데이터
  const [selectedRowData1, setSelectedRowData1] = useState([]); // 지급대장 그리드 클릭 시 데이터
  const [changedRowData1, setChangedRowData1] = useState([]); // 지급대장 그리드 변경 시 데이터
  const [deletedRowData1, setDeletedRowData1] = useState([]);
  const gridRef1 = useRef(); // 지급대장 그리드

  const selectedRowNosRef = useRef(new Set());
  const lastCheckedIndexRef = useRef(null);
  const currentRowIndexRef = useRef(null);
  const [memberModalOpen1, setMemberModalOpen1] = useState(false);

  const [equipOptions, setEquipOptions] = useState([]); // 보호구명 콤보박스
  const memberModalTargetRef1 = useRef(null);
  useEffect(() => {
    // 현재 탭이 2번이 아닌 경우 처리하지 않음
    if (props.value !== 2) return;
    if (!props.searchClick3) return;

    ResetLogic1();

    if (!searchInfo.custCd) {
      props.setSearchClick3(false);
      dispatch(
        showSnackbar({
          message: '사업장을 선택해주세요.',
          type: 'warning',
        })
      );
      return;
    }

    if (!props.validateSelection()) {
      props.setSearchClick3(false);
      return;
    }

    SearchLogic1();
    props.setSearchClick3(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value, props.searchClick3]);

  // 상태값 리셋
  const ResetLogic1 = () => {
    setRowData1([]);
    setSelectedRowData1([]);
    setChangedRowData1([]);
    setDeletedRowData1([]);
  };
  const handleRowSelection = (rowData, rowIndex) => {
    currentRowIndexRef.current = rowIndex;
    setSelectedRowData1(rowData ? [rowData] : []);
  };

  //#region 조회
  // context를 useMemo로 동적으로 계산 (searchInfo.year, searchInfo.custCd 변경 시 자동 업데이트)
  const context = useMemo(
    () => ({
      search: {
        custCd: searchInfo.custCd || '',
        currentYear: searchInfo.year || dayjs().format('YYYY'),
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }),
    [searchInfo.year, searchInfo.custCd]
  );

  const { refetch: Search1 } = useSearch1({
    context: context,
    onSuccess: (rows) => {
      setRowData1(
        rows.map((row) => ({
          ...row,
          issuUserNm: row.issuUserNm || '-',
        }))
      );
    },
  });

  const { refetch: Search2 } = useSearch2({
    onSuccess: (rows) => {
      setEquipOptions(rows);
    },
  });

  // 그리드 조회 함수
  const SearchLogic1 = async () => {
    ResetLogic1();
    try {
      // 최신 context로 refetch (조회연도 변경 시 최신 값 반영)
      // queryKey는 useSearch1의 context에서 자동으로 계산됨
      await Search1();
    } catch (error) {
      // 에러는 onError에서 처리
    }
  };
  //#endregion

  //#region 삭제
  const delete1 = useDelete1();

  const deleteHandler1 = useDelete({
    gridRef: gridRef1,
    deleteMutation: delete1,
    updateDeleted: setDeletedRowData1,
    updateRowData: setRowData1,
    deletedRowData: deletedRowData1,
    rowData: rowData1,
    setChangedRowData: setChangedRowData1,
  });

  const NewYnErrorMsg = useCallback(() => {
    dispatch(
      showSnackbar({
        message: '데이터 입력중입니다. 저장 후 시도하세요.',
        type: 'warning',
      })
    );
  }, [dispatch]);

  const DeleteLogic1 = useCallback(() => {
    const newRowExists = selectedRowData1.some((row) => row.newYn === 'Y');
    if (!newRowExists) {
      deleteHandler1.MarkedForDeletion(selectedRowData1);
    } else {
      NewYnErrorMsg();
    }
  }, [selectedRowData1, deleteHandler1, NewYnErrorMsg]);

  /** 저장 시 삭제 예정 행 일괄 API 호출 (DeletePpeBasic03D1 배치) */
  const RequestDeleteLogic1 = useCallback(
    async (deletedRows) => {
      if (!deletedRows?.length) return null;

      const res = await delete1.runAsync(deletedRows);

      const isError =
        (Array.isArray(res) && res[0]?.type === 'Error') ||
        res?.isSuccess === false ||
        res?.IsSuccess === false;

      if (!isError) {
        setRowData1((prev) =>
          prev.filter((row) => !deletedRows.some((d) => d.no === row.no))
        );
        setDeletedRowData1([]);
      }

      return res;
    },
    [delete1]
  );
  //#endregion

  //#region 저장
  const { runAsync: Save1Async } = useSave1();

  const { run: Save1 } = useSave({
    menuId: 'PPEManagement',
    // requiredLabel: '성명, 보호구명, 수량, 지급인',
    schema: PPE_BASIC_03_Schema,
    getChangedRows: () => changedRowData1.filter((r) => !r.deleteYn),
    getDeletedRows: () => deletedRowData1,
    requestDelete: RequestDeleteLogic1,
    requestSave: (rows) => Save1Async(rows),
    reset: () => {
      setChangedRowData1([]);
      setDeletedRowData1([]);
    },
    onSuccessRefresh: async ({ saveDetailData, changedRows }) => {
      await SearchLogic1();
      if (changedRows?.length) {
        await SendFcmToPpeIssueRecipientsLogic1({
          changedRows,
          saveDetailData,
          userInfo,
          dispatch,
          currentYear: searchInfo.year || dayjs().format('YYYY'),
        });
      }
    },
  });
  //#endregion

  //#region 회원선택(성명·지급인)
  const GetDefaultNewRow1 = useCallback(() => {
    const custCd = searchInfo.custCd || '';
    return {
      issuId: '',
      userId: '',
      userDepCd: '',
      userDepNm: '',
      userRank: '',
      userNm: '',
      equipNm: '',
      reason: '',
      createDate: dayjs().format('YYYY-MM-DD HH:mm'),
      qty: null,
      issuSign: '',
      issuSignPath: '',
      stockQty: 0,
      managerDepartment: '',
      managerDepNmLast: '',
      issuUserId: '',
      issuUserNm: '',
      remark: '',
      custCd,
      erCd: '',
    };
  }, [searchInfo.custCd]);

  const MemberSelectPopupClick1 = useCallback((params, columnKey) => {
    if (params.data?.newYn !== 'Y') return;
    memberModalTargetRef1.current = {
      columnKey,
      rowNo: params.data?.no,
    };
    setMemberModalOpen1(true);
  }, []);

  /** 신규 행만 제거 (NO 컬럼 삭제 아이콘). 트랜잭션으로만 추가된 행은 prev에 없으므로 삭제 후 노드 전체로 rowData 동기화 */
  const DeleteNewRow1 = useCallback((row) => {
    const api = gridRef1.current?.api;
    if (!api || row?.no == null) return;

    api.applyTransaction({ remove: [row] });

    const remaining = [];
    api.forEachNode((node) => {
      if (node?.data) remaining.push({ ...node.data });
    });
    setRowData1(remaining);

    setChangedRowData1((prev) => prev.filter((r) => r.no !== row.no));
    setSelectedRowData1((prev) => (prev ?? []).filter((r) => r.no !== row.no));
    selectedRowNosRef.current.delete(row.no);
  }, []);

  const RestoreRow1 = deleteHandler1.RestoreRow;

  const HandleMemberSelection1 = useCallback(
    (selected) => {
      if (selected == null) return;
      const member = Array.isArray(selected) ? selected[0] : selected;
      if (!member?.id) return;

      const target = memberModalTargetRef1.current;
      const api = gridRef1.current?.api;
      if (!target?.rowNo || !api) return;

      const node = api.getRowNode(target.rowNo);
      if (!node?.data) return;

      const prev = node.data;
      let updated;

      if (target.columnKey === 'userNm') {
        const allowedEquip = FilterEquipOptionsByUserDepCd(
          equipOptions,
          member.depCd
        );
        const prevErCd = prev.erCd;
        const stillValid =
          !prevErCd ||
          allowedEquip.some((o) => String(o.erCd) === String(prevErCd));
        updated = {
          ...prev,
          userNm: member.name,
          userId: member.id,
          userDepCd: member.depCd,
          userDepNm: member.depNm,
          userRank: member.rank,
          editYn: 'Y',
          ...(stillValid
            ? {}
            : {
                equipNm: '',
                erCd: '',
              }),
        };
      } else if (target.columnKey === 'issuUserNm') {
        updated = {
          ...prev,
          issuUserNm: member.name,
          issuUserId: member.id,
          editYn: 'Y',
        };
      } else {
        return;
      }

      node.setData(updated);
      setChangedRowData1((ch) => {
        const filtered = ch.filter((row) => row.no !== updated.no);
        return [...filtered, updated];
      });
      memberModalTargetRef1.current = null;
    },
    [setChangedRowData1, equipOptions]
  );
  //#endregion

  //#region 나머지 함수
  //인쇄 함수
  const PrintLogic1 = async (_value1) => {
    dispatch(showLoading());
    try {
      // 검색 연도 추출 (currentYear 사용)
      const currentYear = searchInfo.year;
      let year = '';

      if (currentYear) {
        year = currentYear.toString();
      } else {
        // 날짜가 없으면 현재 연도 사용
        year = dayjs().year().toString();
      }

      // HeaderData에 선택한 연도 추가
      const headerData = {
        YEAR: year,
      };

      // 천단위 구분 기호 추가 함수
      const formatNumber = (value) => {
        if (value == null || value === '') return '0';
        const num = typeof value === 'number' ? value : Number(value);
        return isNaN(num) ? '0' : num.toLocaleString('ko-KR');
      };

      // TableData에 그리드 데이터 매핑 (레포트 양식에 맞게)
      const tableData = await Promise.all(
        rowData1.map(async (row, index) => {
          // 서명 이미지 처리 (issuSign이 'Y'이고 서명 경로가 있는 경우)
          let signImageBase64 = '';
          if (row.issuSign === 'Y' && row.issuSignPath) {
            const signPath = Commons.EncodeUrl(row.issuSignPath);
            const signUrl =
              Global.file.useUrl +
              'files/' +
              signPath +
              '?t=' +
              new Date().getTime();
            const response = await fetch(signUrl);
            if (response.ok) {
              const blob = await response.blob();
              const base64Image = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });

              if (base64Image && base64Image.startsWith('data:')) {
                signImageBase64 = base64Image.replace(
                  /^data:image\/[a-z]+;base64,/,
                  ''
                );
              }
            }
          }

          // 날짜 형식 변환 (camelCase 필드명 사용)
          const createDate = row.createDate
            ? dayjs(row.createDate).format('YYYY-MM-DD')
            : '';

          return {
            no: index + 1,
            USER_NM: row.userNm || '',
            EQUIP_NM: row.equipNm || '',
            REASON: row.reason || '',
            CREATE_DATE: createDate,
            QTY: formatNumber(row.qty),
            ISSU_SIGN: signImageBase64, // Base64 이미지 문자열
            STOCK_QTY: formatNumber(row.stockQty),
            MANAGE_R_DEP_NM_LAST: row.managerDepNmLast || '',
            MANAGE_R_NM: row.managerNm || '',
            REMARK: row.remark || '',
          };
        })
      );

      const printData = {
        FileName: 'PPE_BASIC_01',
        HeaderData: JSON.stringify(headerData),
        TableData: JSON.stringify(tableData),
      };

      await Commons.Axios(
        'report',
        Global.base.useUrl + Global.urls.Reports.reports,
        printData
      ).then((response) => {
        const blobUrl = URL.createObjectURL(response.data);
        window.open(blobUrl, '_blank'); // 새 탭에서 열기
      });
    } catch (e) {
      console.log(e);

      dispatch(
        showSnackbar({
          message: e.message,
          type: 'error',
        })
      );
    } finally {
      dispatch(closeLoading());
    }
  };
  //엑셀다운로드 버튼 클릭 시 (서명 컬럼은 issuSignPath 이미지 삽입)
  const ExcelDownload = async () => {
    if (!rowData1?.length) {
      dispatch(
        showSnackbar({
          message: '다운로드할 데이터가 없습니다. 조회 후 이용해 주세요.',
          type: 'warning',
        })
      );
      return;
    }

    dispatch(showLoading());
    try {
      const buffer = await BuildPpeBasic03LedgerExcelBuffer(rowData1);
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const fileName = `${dayjs().format('YYYYMMDDHHmm')}보호구지급대장.xlsx`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(link.href);
      dispatch(
        showSnackbar({
          message: '엑셀 다운로드가 완료되었습니다.',
          type: 'success',
        })
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: `엑셀 다운로드 중 오류가 발생했습니다: ${error?.message || error}`,
          type: 'error',
        })
      );
    } finally {
      dispatch(closeLoading());
    }
  };
  //#endregion

  return (
    <>
      <PPE_BASIC_03
        value={props.value}
        navigate={navigate}
        userInfo={userInfo}
        rowData1={rowData1}
        setRowData1={setRowData1}
        selectedRowData1={selectedRowData1}
        setSelectedRowData1={setSelectedRowData1}
        changedRowData1={changedRowData1}
        setChangedRowData1={setChangedRowData1}
        filterClick3={props.filterClick3}
        setFilterClick3={props.setFilterClick3}
        searchClick3={props.searchClick3}
        setSearchClick3={props.setSearchClick3}
        addClick3={props.addClick3}
        setAddClick3={props.setAddClick3}
        saveClick3={props.saveClick3}
        setSaveClick3={props.setSaveClick3}
        deleteClick3={props.deleteClick3}
        setDeleteClick3={props.setDeleteClick3}
        printClick3={props.printClick3}
        setPrintClick3={props.setPrintClick3}
        listClick3={props.listClick3}
        setListClick3={props.setListClick3}
        dateValue1={props.dateValue1}
        setDateValue1={props.setDateValue1}
        dateValue2={props.dateValue2}
        setDateValue2={props.setDateValue2}
        searchSiteCd={props.searchSiteCd}
        gridRef1={gridRef1}
        selectedRowNosRef={selectedRowNosRef}
        lastCheckedIndexRef={lastCheckedIndexRef}
        // 함수
        validateSelection={props.validateSelection}
        ResetLogic1={ResetLogic1}
        SearchLogic1={SearchLogic1}
        SaveLogic1={Save1}
        DeleteLogic1={DeleteLogic1}
        MarkedForDeletion1={deleteHandler1.MarkedForDeletion}
        NewYnErrorMsg={NewYnErrorMsg}
        PrintLogic1={PrintLogic1}
        ExcelDownload={ExcelDownload}
        handleRowSelection={handleRowSelection}
        MemberSelectPopupClick1={MemberSelectPopupClick1}
        DeleteNewRow1={DeleteNewRow1}
        RestoreRow1={RestoreRow1}
        getDefaultNewRow1={GetDefaultNewRow1}
        equipOptions={equipOptions}
      />
      <MemberInfoModal
        open={memberModalOpen1}
        onClose={() => {
          setMemberModalOpen1(false);
          memberModalTargetRef1.current = null;
        }}
        onSelection={HandleMemberSelection1}
      />
    </>
  );
}

export default PPE_BASIC_03F;
